import { LoadingManager, PMREMGenerator, Texture, WebGLRenderer } from 'three'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader'
import { getResourcePath } from '../API/storageAPI'
import { materialToEnvironmentMap } from './materialsConfiguration'

export interface IEnvironment {
  map: Texture
  intensity: number
}

export interface IEnvironmentHelper {
  getEnvironmentForMaterial: (
    materialName: string,
    progressCallback?: (progress: number) => void,
  ) => Promise<IEnvironment | undefined>
  preloadDefaultEnvironment: (progressCallback?: (progress: number) => void) => Promise<IEnvironment>
  loadEnvironmentFromPath: (path: string) => Promise<IEnvironment>
}

export function getEnvironmentHelper(renderer: WebGLRenderer): IEnvironmentHelper {
  const pmremGenerator = new PMREMGenerator(renderer)
  pmremGenerator.compileEquirectangularShader()

  async function loadEnvironmentFromPath(
    path: string,
    progressCallback?: (progress: number) => void,
  ): Promise<IEnvironment> {
    const loadingManager = new LoadingManager(undefined, (url, loaded, total) => {
      if (progressCallback) progressCallback(loaded / total)
    })

    const environmentTexture = await new RGBELoader(loadingManager).loadAsync(path)
    const generatedEnvTextureTarget = pmremGenerator.fromEquirectangular(environmentTexture)

    return {
      map: generatedEnvTextureTarget.texture,
      intensity: 0.5,
    }
  }

  async function preloadDefaultEnvironment(
    progressCallback?: (progress: number) => void,
  ): Promise<IEnvironment> {
    const path = getResourcePath('environment_new_dark.hdr')
    return getFromCacheOrLoad(path, progressCallback)
  }

  const cachedEnvironmentMap: { [key: string]: IEnvironment } = {}

  async function getEnvironmentForMaterial(
    materialName: string,
    progressCallback?: (progress: number) => void,
  ): Promise<IEnvironment | undefined> {
    if (materialName.toLowerCase() in materialToEnvironmentMap) {
      const path = getResourcePath(materialToEnvironmentMap[materialName.toLowerCase()])
      return getFromCacheOrLoad(path, progressCallback)
    }
  }

  async function getFromCacheOrLoad(path: string, progressCallback?: (progress: number) => void){
    const loadedEnv = cachedEnvironmentMap[path]
    if (loadedEnv) return loadedEnv
    const env = await loadEnvironmentFromPath(
      path,
      progressCallback,
    )
    cachedEnvironmentMap[path] = env
    return env
  }

  return { getEnvironmentForMaterial: getEnvironmentForMaterial, preloadDefaultEnvironment, loadEnvironmentFromPath }
}
